import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useToast } from "@chakra-ui/react";
import { Email } from "features/Resources/Email/models";
import { apiEmailById, v2ApiNewHireEmailsPathById } from "helpers/url/api/v2";
import { NewHireJourneyId } from "models/newHire";
import { useNetworkRelationDetailQuery } from "features/RecordTarget/hooks";
import { useUserDetailQuery } from "features/User/hooks";
import { EmailRecipient } from "./models/EmailRecipient";
import { recipientToDisplayName, recipientToDisplayValue } from "./helpers";

export const emailKeys = {
  all: ["emails"] as const,
  lists: () => [...emailKeys.all, "list"] as const,
  list: (filters: string) => [...emailKeys.lists(), { filters }] as const,
  details: () => [...emailKeys.all, "detail"] as const,
  detail: (id: string) => [...emailKeys.details(), id] as const,
  byNewHireJourneys: () => [...emailKeys.all, "newHireJourneys"] as const,
  byNewHireJourney: (id: string) => [...emailKeys.byNewHireJourneys(), id] as const,
};

export function setNewEmailInQueryClient(queryClient: QueryClient, newEmail: Email) {
  queryClient.setQueryData(emailKeys.detail(newEmail.id), (oldData: Email | undefined) => {
    if (!oldData) {
      return newEmail;
    }
    return {
      ...oldData,
      ...newEmail,
    };
  });
}

export const useEmailDetailQuery = (id: string | undefined, options = {}) => useQuery<Email | null>({
  queryKey: emailKeys.detail(id as string),
  queryFn: async () => apiClient.get(apiEmailById(id as string)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

export const useEmailListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<Email[]>({
    queryKey: queryString ? emailKeys.list(queryString) : emailKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.emails.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

export const useNewHireJourneyEmailsQuery = (newHireJourneyId: NewHireJourneyId, options = {}) => useQuery<Email[]>({
  queryKey: emailKeys.byNewHireJourney(newHireJourneyId),
  queryFn: async () => apiClient.get(v2ApiNewHireEmailsPathById(newHireJourneyId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!newHireJourneyId && ((options as { enabled?: boolean }).enabled ?? true),
});

function useEmailRequest(mutationFn: (data: Email) => Promise<any>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      setNewEmailInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useUpdateEmail(emailId: string) {
  return useEmailRequest(async (data: Email) => apiClient.put<Email>(apiEmailById(emailId), { email: data }));
}

export function useCreateEmail() {
  return useEmailRequest(async (data: Email) => apiClient.post<Email>(API_ROUTES.emails.base, { email: data }));
}

type EnabledOption = {
  enabled: boolean
};

export function useEmailRecipientDetails(recipient: EmailRecipient, options: EnabledOption = { enabled: true }) {
  // Always use the ID fields directly - simpler and more consistent
  const userId = (recipient as any)?.userId;
  const relationId = (recipient as any)?.onboardingJourneyNetworkRelationId;

  // Fetch data by ID - React Query will handle caching
  const { data: userData } = useUserDetailQuery(userId, {
    enabled: options.enabled && !!userId,
  });

  const { data: relationData } = useNetworkRelationDetailQuery(relationId, {
    enabled: options.enabled && !!relationId,
  });

  if (!recipient || !options.enabled) {
    return { displayName: "", tagName: "" };
  }

  // displayName (without email type) for avatars
  const displayName = recipientToDisplayName(recipient, userData as any, relationData);
  // tagName (with email type) for tags in popover
  const tagName = recipientToDisplayValue(recipient, userData as any, relationData);
  const role = (recipient as any)?.role;
  return { displayName, tagName, src: (userData as any)?.avatarUrl, role };
}
